import { SerializedStyles, css } from '@emotion/react'
import { KeyboardEvent, ReactNode, SyntheticEvent, useState } from 'react'
import { Menu as SimpleMenu } from '@mui/material'
import MenuItem, { MenuItemProps } from '@mui/material/MenuItem'

import { MenuButton } from '.'
import { hoverColor } from '../../../lib/styles/colors'
import { focusVisibleWithBackground } from '../../../lib/styles/focusIndicator'
import { uniqueId } from '../../../lib/utils'

type MenuProps = {
  /** Accessible label applied to the menu button */
  ariaLabel?: string
  /** Optional styles to add to the icon button */
  buttonCss?: SerializedStyles
  /** Label for checkbox menu */
  checkboxMenuHeader?: string | ReactNode
  /** Optional class to override default emotion styles */
  className?: string
  /** Indicates if the tooltip is shown on hover, defaults to true */
  displayTooltip?: boolean
  /** Icon for a menu without a label, defaults to MoreVert */
  icon?: 'Arrow' | 'ControlPoint' | 'FilterList' | 'MoreVert'
  /** Icon color variant, defaults to Secondary */
  iconColorVariant?: 'Primary' | 'Secondary'
  /** Id of control */
  id: string
  /** Optional label for button. If not provided, will fallback to icon */
  label?: string
  /** List of menu items */
  menuItems: Array<MenuItemProps>
  /** Text shown when hovering over the menu button. If not set, ariaLabel is used. */
  tooltip?: string
}

const menuContainerCss = css`
  display: inline;
`

const menuCss = css`
  .MuiPopover-paper {
    left: 18px;
    min-width: 160px;
  }

  .MuiMenuItem-root {
    &:hover,
    &:focus-within {
      background-color: ${hoverColor};
    }

    ${focusVisibleWithBackground(hoverColor)}
  }
`

const Menu = ({
  ariaLabel,
  buttonCss,
  checkboxMenuHeader: _checkboxMenuHeader,
  className,
  displayTooltip = true,
  icon = 'MoreVert',
  iconColorVariant = 'Secondary',
  id,
  label,
  menuItems,
  tooltip
}: MenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleClick = (event: SyntheticEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowDown') {
      setAnchorEl(event.currentTarget)
    }
  }

  const handleItemClick = (onClick: any) => {
    handleClose()

    if (onClick) {
      onClick()
    }
  }

  const renderMenuItems = () =>
    menuItems.map((item: any) => {
      // WCAG 4.1.2: Ensure all menu items have accessible names
      // Use explicit ariaLabel if provided, otherwise rely on text content
      // Note: MUI MenuItem automatically uses text content as accessible name
      // We only add aria-label when explicitly provided or when text is non-string (e.g., React element)
      const needsExplicitLabel = item.ariaLabel || typeof item.text !== 'string'
      const accessibleLabel =
        item.ariaLabel || (typeof item.text === 'string' ? item.text : 'Menu option')

      return (
        <MenuItem
          {...(needsExplicitLabel && { 'aria-label': accessibleLabel })}
          disabled={item.disabled}
          key={`item-${uniqueId()}`}
          onClick={() => handleItemClick(item.onClick)}
        >
          {item.text}
        </MenuItem>
      )
    })

  return (
    <div className={className} css={menuContainerCss}>
      <MenuButton
        ariaLabel={ariaLabel}
        buttonCss={buttonCss}
        displayTooltip={displayTooltip}
        label={label}
        icon={icon}
        iconColorVariant={iconColorVariant}
        expanded={Boolean(anchorEl)}
        menuId={id}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tooltip={tooltip}
      />
      <SimpleMenu
        id={id}
        css={menuCss}
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        {renderMenuItems()}
      </SimpleMenu>
    </div>
  )
}

export default Menu
